import SEO from '@/components/common/SEO';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Calendar,
  Clock,
  Award,
  Users,
  Play,
  PlayCircle,
  CheckCircle,
  Share2,
  ArrowLeft,
  ArrowRight,
  Star,
  Globe,
  Loader2,
  Copy,
  CalendarPlus,
  Facebook,
  Linkedin,
  Twitter,
  Mail,
  Monitor,
  Video,
  HardDrive,
  Building2,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Target,
  UserCheck,
  FileText,
  Hash,
  Signal,
  ShoppingCart,
  Timer,
  Hourglass,
  Sparkles,
} from 'lucide-react';
import { webinarsAPI, registrationsAPI, checkoutAPI } from '@/services/api';
import { format } from 'date-fns';
import { formatET, getETAbbr } from '@/utils/dateUtils';
import { toast } from 'sonner';
import { WebinarCard } from '@/components/common/WebinarCard';
import { Breadcrumb } from '@/components/common/Breadcrumb';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to resolve image URLs (handles both absolute and relative paths)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

// HR Industry IDs - CPE credits only show for HR industry
const HR_INDUSTRY_NAMES = ['HR', 'Human Resource', 'Human Resources', 'Cross Industry Functions', 'HR Compliance'];

// HRCI Logo URL - from R2
const HRCI_LOGO_URL = 'https://pub-e7421450cec4415a903c8aa1c08efe15.r2.dev/gallery/hrci-logo.svg';

const levelColors = {
  basic: 'bg-green-100 text-green-700',
  intermediate: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
};

const levelLabels = {
  basic: 'Basic',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

// Helper to check if webinar is HR industry (for CPE badge display)
const isHRIndustry = (industryName, subIndustryName) => {
  const namesToCheck = [industryName, subIndustryName].filter(Boolean);
  if (namesToCheck.length === 0) return false;
  return namesToCheck.some(name => 
    HR_INDUSTRY_NAMES.some(hr => name.toLowerCase().includes(hr.toLowerCase()))
  );
};

// Timezone abbreviation mapping
const timezoneAbbreviations = {
  'America/New_York': 'EST',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'America/Los_Angeles': 'PST',
  'America/Phoenix': 'MST',
  'America/Anchorage': 'AKST',
  'Pacific/Honolulu': 'HST',
  'America/Detroit': 'EST',
  'America/Indiana/Indianapolis': 'EST',
  'America/Kentucky/Louisville': 'EST',
  'America/Toronto': 'EST',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Asia/Tokyo': 'JST',
  'Asia/Shanghai': 'CST',
  'Asia/Kolkata': 'IST',
  'Australia/Sydney': 'AEDT',
  'UTC': 'UTC',
};

// Helper to get timezone abbreviation
const getTimezoneAbbr = (timezone) => {
  if (!timezone) return 'EST';
  // If already an abbreviation (3-4 letters), return as is
  if (/^[A-Z]{2,5}$/.test(timezone)) return timezone;
  // Look up in mapping
  return timezoneAbbreviations[timezone] || timezone.split('/').pop().replace('_', ' ');
};

// Industry-based fallback images (Corporate Training Style - Generated per industry)
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const industryImages = {
  'Cross Industry Functions': `${BASE_URL}/api/static/images/industries/cross_industry_functions_1dc8cb34.png`,
  'Healthcare and Hospital': `${BASE_URL}/api/static/images/industries/healthcare_and_hospital_40b680e4.png`,
  'Healthcare': `${BASE_URL}/api/static/images/industries/healthcare_and_hospital_40b680e4.png`,
  'Banking Financial Services and Insurance': `${BASE_URL}/api/static/images/industries/banking_financial_services_and_insurance_5dc7a75a.png`,
  'Manufacturing': `${BASE_URL}/api/static/images/industries/manufacturing_4fbaa269.png`,
  'Construction': `${BASE_URL}/api/static/images/industries/construction_55779e1e.png`,
  'Legal and Law': `${BASE_URL}/api/static/images/industries/legal_and_law_e7a3978e.png`,
  'Legal': `${BASE_URL}/api/static/images/industries/legal_and_law_e7a3978e.png`,
  'Life Sciences': `${BASE_URL}/api/static/images/industries/life_sciences_e1dad885.png`,
  'Banks and Credit Unions': `${BASE_URL}/api/static/images/industries/banks_and_credit_unions_325709aa.png`,
  'Banking': `${BASE_URL}/api/static/images/industries/banks_and_credit_unions_325709aa.png`,
  'Financial Services and Trading': `${BASE_URL}/api/static/images/industries/financial_services_and_trading_89ceb2d8.png`,
  'Financial': `${BASE_URL}/api/static/images/industries/financial_services_and_trading_89ceb2d8.png`,
  'Insurance': `${BASE_URL}/api/static/images/industries/insurance_e60007e4.png`,
  'Medical Devices': `${BASE_URL}/api/static/images/industries/medical_devices_179555d1.png`,
  'Drugs & Chemicals': `${BASE_URL}/api/static/images/industries/drugs_chemicals_1e4925e4.png`,
  'Biotechnology': `${BASE_URL}/api/static/images/industries/biotechnology_c1285ae0.png`,
  'Clinical Trial and Clinical Research': `${BASE_URL}/api/static/images/industries/clinical_trial_and_clinical_research_f072cf4a.png`,
  'Clinical': `${BASE_URL}/api/static/images/industries/clinical_trial_and_clinical_research_f072cf4a.png`,
  'Laboratory': `${BASE_URL}/api/static/images/industries/laboratory_1900608a.png`,
  'Food and Dietary Supplements': `${BASE_URL}/api/static/images/industries/food_and_dietary_supplements_2a4f16b1.png`,
  'All FDA Regulated Industry': `${BASE_URL}/api/static/images/industries/all_fda_regulated_industry_4b39b1e0.png`,
  'FDA': `${BASE_URL}/api/static/images/industries/all_fda_regulated_industry_4b39b1e0.png`,
  'Pharmaceutical': `${BASE_URL}/api/static/images/industries/pharmaceutical_055fbb4f.png`,
  'Quality and Safety': `${BASE_URL}/api/static/images/industries/quality_and_safety_74265bc8.png`,
  'Quality': `${BASE_URL}/api/static/images/industries/quality_and_safety_74265bc8.png`,
  'Standards and Methodology': `${BASE_URL}/api/static/images/industries/standards_and_methodology_6a88d70c.png`,
  'Quality Management': `${BASE_URL}/api/static/images/industries/quality_management_351b80cf.png`,
  'Medicaid and Medicare': `${BASE_URL}/api/static/images/industries/medicaid_and_medicare_a96df4ca.png`,
  'HIPAA / HITECH': `${BASE_URL}/api/static/images/industries/hipaa_hitech_5db49ef2.png`,
  'HIPAA': `${BASE_URL}/api/static/images/industries/hipaa_hitech_5db49ef2.png`,
  'Audits & Data Security': `${BASE_URL}/api/static/images/industries/audits_data_security_50fb4653.png`,
  'Hospital Management': `${BASE_URL}/api/static/images/industries/hospital_management_3ab9dc5e.png`,
  'Corporate Governance': `${BASE_URL}/api/static/images/industries/corporate_governance_ab00e884.png`,
  'IT and PCI Compliance': `${BASE_URL}/api/static/images/industries/it_and_pci_compliance_78427d1a.png`,
  'IT': `${BASE_URL}/api/static/images/industries/it_and_pci_compliance_78427d1a.png`,
  'HR Compliance': `${BASE_URL}/api/static/images/industries/hr_compliance_b55d1eb0.png`,
  'HR': `${BASE_URL}/api/static/images/industries/hr_compliance_b55d1eb0.png`,
  'OSHA Compliance': `${BASE_URL}/api/static/images/industries/osha_compliance_59ebc134.png`,
  'OSHA': `${BASE_URL}/api/static/images/industries/osha_compliance_59ebc134.png`,
  'EHS Compliance': `${BASE_URL}/api/static/images/industries/ehs_compliance_5156d634.png`,
  'Trade and Logistics': `${BASE_URL}/api/static/images/industries/trade_and_logistics_747e3358.png`,
  'Accounting and Taxation': `${BASE_URL}/api/static/images/industries/accounting_and_taxation_303fa4d6.png`,
  'Accounting': `${BASE_URL}/api/static/images/industries/accounting_and_taxation_303fa4d6.png`,
  'Payroll Benefits and Compensation': `${BASE_URL}/api/static/images/industries/payroll_benefits_and_compensation_a99ed275.png`,
  'Payroll': `${BASE_URL}/api/static/images/industries/payroll_benefits_and_compensation_a99ed275.png`,
  'Excel Training': `${BASE_URL}/api/static/images/industries/excel_training_36a022f1.png`,
  'Excel': `${BASE_URL}/api/static/images/industries/excel_training_36a022f1.png`,
  'Outlook Training': `${BASE_URL}/api/static/images/industries/outlook_training_c2a7a416.png`,
  'Outlook': `${BASE_URL}/api/static/images/industries/outlook_training_c2a7a416.png`,
  'Sales And Customer Service Coaching': `${BASE_URL}/api/static/images/industries/sales_and_customer_service_coaching_bdd251e3.png`,
  'Sales': `${BASE_URL}/api/static/images/industries/sales_and_customer_service_coaching_bdd251e3.png`,
  'Project Management': `${BASE_URL}/api/static/images/industries/project_management_29eb7e60.png`,
  'Risk Management': `${BASE_URL}/api/static/images/industries/risk_management_68769a2e.png`,
  'Communication and Business Writing': `${BASE_URL}/api/static/images/industries/communication_and_business_writing_0917d5ed.png`,
  'Communication': `${BASE_URL}/api/static/images/industries/communication_and_business_writing_0917d5ed.png`,
  'Public Housing & Compliance': `${BASE_URL}/api/static/images/industries/public_housing_compliance_3c8da890.png`,
  'Law Reform': `${BASE_URL}/api/static/images/industries/law_reform_ce6ff262.png`,
  'Law Enforcement': `${BASE_URL}/api/static/images/industries/law_enforcement_514a4fb6.png`,
  'Generic Webinars': `${BASE_URL}/api/static/images/industries/generic_webinars_4eb618cf.png`,
  'default': `${BASE_URL}/api/static/images/industries/generic_webinars_4eb618cf.png`
};

// Get appropriate thumbnail for webinar based on industry
const getWebinarThumbnail = (thumbnailUrl, industryName) => {
  // Check if thumbnail URL is valid (not empty, not pointing to old preview domain)
  if (thumbnailUrl && !thumbnailUrl.includes('preview.emergentagent.com') && !thumbnailUrl.includes('undefined')) {
    return thumbnailUrl;
  }
  
  // Find exact match first
  if (industryName && industryImages[industryName]) {
    return industryImages[industryName];
  }
  
  // Find partial match
  if (industryName) {
    for (const [key, url] of Object.entries(industryImages)) {
      if (industryName.toLowerCase().includes(key.toLowerCase()) || 
          key.toLowerCase().includes(industryName.toLowerCase())) {
        return url;
      }
    }
  }
  
  return industryImages.default;
};

export default function WebinarDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { addToCart, loading: cartLoading } = useCart();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState('live');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  
  // Manual accordion state - user clicks to expand/collapse
  const [activeSection, setActiveSection] = useState('overview');
  const accordionRef = useRef(null);
  
  // Carousel state for speaker and industry webinars
  const [speakerCarouselIndex, setSpeakerCarouselIndex] = useState(0);
  const [industryCarouselIndex, setIndustryCarouselIndex] = useState(0);

  useEffect(() => {
    fetchWebinar();
    checkRegistration();
  }, [id]);

  const fetchWebinar = async () => {
    try {
      const response = await webinarsAPI.getById(id);
      setWebinar(response.data);
    } catch (error) {
      console.error('Error fetching webinar:', error);
      toast.error('Failed to load webinar details');
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await registrationsAPI.getMy();
      const registered = response.data.some((reg) => reg.webinar_id === id);
      setIsRegistered(registered);
    } catch (error) {
      console.error('Error checking registration:', error);
    }
  };

  const handleRegister = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/webinars/${id}` } });
      return;
    }

    // Map pricing option to variant - use standardized variant names with participant info
    const variantMap = {
      'live': 'Live Access for 1 Participant',
      'recorded': 'Download Access for 1 Participant',
      'live_recorded': 'Live + Download Access for 1 Participant',
      'digital': 'USB Flash Drive for 1 Participant',
      'live_usb': 'Live + USB Flash Drive for 1 Participant',
      'group': 'Group Live (Up to 20 Participants)',
    };
    
    const variant = variantMap[selectedPricing] || 'Live Access for 1 Participant';
    
    setAddingToCart(true);
    const result = await addToCart('webinar', webinar.id, variant);
    setAddingToCart(false);
    
    if (result.success) {
      // Navigate to cart
      navigate('/cart');
    } else if (result.requiresAuth) {
      navigate('/login', { state: { from: `/webinars/${id}` } });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return formatET(dateStr, 'EEEE, MMMM dd, yyyy');
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return formatET(dateStr, 'h:mm a');
  };

  const copyWebinarCode = () => {
    if (webinar?.webinar_code) {
      navigator.clipboard.writeText(webinar.webinar_code);
      toast.success('Webinar code copied!');
    }
  };

  const addToGoogleCalendar = () => {
    if (!webinar?.scheduled_date) return;
    
    const startDate = new Date(webinar.scheduled_date);
    const endDate = new Date(startDate.getTime() + webinar.duration_minutes * 60000);
    
    const formatGoogleDate = (date) => date.toISOString().replace(/-|:|\.\d{3}/g, '');
    
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.set('action', 'TEMPLATE');
    url.searchParams.set('text', webinar.title);
    url.searchParams.set('dates', `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`);
    url.searchParams.set('details', webinar.short_description || webinar.description?.slice(0, 200));
    url.searchParams.set('location', 'Online Webinar');
    
    window.open(url.toString(), '_blank');
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const title = webinar?.title || '';
    const text = webinar?.short_description || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + '\n\n' + url)}`,
    };
    
    window.open(shareUrls[platform], '_blank');
    setShowShareMenu(false);
  };

  const getPricingOptions = () => {
    if (!webinar) return [];
    
    const options = [];
    const isLiveWebinar = webinar.webinar_type === 'live';
    
    // For Live webinars
    if (isLiveWebinar) {
      if (webinar.price_live > 0 || webinar.price > 0) {
        options.push({
          id: 'live',
          name: 'Live Access',
          description: 'Join the live webinar session',
          price: webinar.price_live || webinar.price,
          icon: Monitor,
          features: ['Live participation', 'Q&A session', 'Certificate of completion', 'CPE credits'],
          participantInfo: 'For 1 Participant',
        });
      }
      
      if (webinar.price_recorded > 0) {
        options.push({
          id: 'recorded',
          name: 'Download Access',
          description: 'Watch at your convenience',
          price: webinar.price_recorded,
          icon: Video,
          features: ['On-demand access', 'Lifetime availability', 'Certificate of completion', 'CPE credits'],
          participantInfo: 'For 1 Participant',
        });
      }
      
      if (webinar.price_live_recorded > 0) {
        options.push({
          id: 'live_recorded',
          name: 'Live + Download Access',
          description: 'Best of both worlds',
          price: webinar.price_live_recorded,
          icon: PlayCircle,
          features: ['Live participation', 'On-demand access after', 'Q&A session', 'CPE credits'],
          participantInfo: 'For 1 Participant',
        });
      }
      
      if (webinar.price_digital_download > 0) {
        options.push({
          id: 'digital',
          name: 'USB Flash Drive',
          description: 'Receive recording on USB',
          price: webinar.price_digital_download,
          icon: HardDrive,
          features: ['USB flash drive', 'Reference materials', 'Keep forever', 'CPE credits'],
          participantInfo: 'For 1 Participant',
        });
      }
      
      if (webinar.price_live_usb > 0) {
        options.push({
          id: 'live_usb',
          name: 'Live + USB Flash Drive',
          description: 'Live session + USB recording',
          price: webinar.price_live_usb,
          icon: HardDrive,
          features: ['Live participation', 'USB flash drive after', 'Q&A session', 'CPE credits'],
          participantInfo: 'For 1 Participant',
        });
      }
      
      if (webinar.price_group_live > 0) {
        options.push({
          id: 'group',
          name: 'Group Live (Up to 20)',
          description: 'Corporate group access',
          price: webinar.price_group_live,
          icon: Building2,
          features: ['Up to 20 participants', 'Live participation', 'Bulk certificates', 'Corporate licensing'],
          participantInfo: 'Up to 20 Participants',
        });
      }
    } else {
      // For Recorded/On-Demand webinars
      if (webinar.price_recorded > 0 || webinar.price > 0) {
        options.push({
          id: 'recorded',
          name: 'Download Access',
          description: 'Watch at your convenience',
          price: webinar.price_recorded || webinar.price,
          icon: Video,
          features: ['On-demand access', 'Lifetime availability', 'Certificate of completion', 'CPE credits'],
          participantInfo: 'For 1 Participant',
        });
      }
      
      if (webinar.price_digital_download > 0) {
        options.push({
          id: 'digital',
          name: 'USB Flash Drive',
          description: 'Receive recording on USB',
          price: webinar.price_digital_download,
          icon: HardDrive,
          features: ['USB flash drive', 'Reference materials', 'Keep forever', 'CPE credits'],
          participantInfo: 'For 1 Participant',
        });
      }
    }
    
    // Fallback if no options
    if (options.length === 0) {
      options.push({
        id: 'live',
        name: 'Access',
        description: 'Get access to this webinar',
        price: webinar.price || 0,
        icon: Monitor,
        features: ['Full access', 'Certificate of completion', 'CPE credits'],
      });
    }
    
    return options;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  if (!webinar) {
    return (
      <div className="min-h-screen bg-white pt-24">
        <div className="container-custom py-20 text-center">
          <h1 className="font-heading text-2xl text-navy-700 mb-4">Webinar not found</h1>
          <Link to="/webinars">
            <Button variant="outline" className="rounded-xl">Back to Webinars</Button>
          </Link>
        </div>
      </div>
    );
  }

  const pricingOptions = getPricingOptions();
  const selectedOption = pricingOptions.find(p => p.id === selectedPricing) || pricingOptions[0];

  return (
    <div className="min-h-screen bg-transparent" data-testid="webinar-detail-page">
      <SEO
        title={webinar.title}
        description={(webinar.short_description || webinar.description || `${webinar.title} — an expert-led GRC webinar by GRC Educators.`).replace(/<[^>]*>/g, '').slice(0, 160)}
        path={`/webinars/${webinar.slug || webinar.id}`}
        type="article"
        image={webinar.thumbnail_url && webinar.thumbnail_url.startsWith('http') ? webinar.thumbnail_url : undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          "name": webinar.title,
          "description": (webinar.short_description || webinar.description || '').replace(/<[^>]*>/g, '').slice(0, 300),
          "startDate": webinar.scheduled_date || undefined,
          "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
          "eventStatus": "https://schema.org/EventScheduled",
          "location": {
            "@type": "VirtualLocation",
            "url": `https://grceducators.com/webinars/${webinar.slug || webinar.id}`
          },
          "organizer": {
            "@type": "Organization",
            "name": "GRC Educators",
            "url": "https://grceducators.com"
          },
          ...(webinar.price ? {
            "offers": {
              "@type": "Offer",
              "price": webinar.price,
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": `https://grceducators.com/webinars/${webinar.slug || webinar.id}`
            }
          } : {})
        }}
      />
      {/* Hero Section - MailCraft grid canvas */}
      <section className="relative pt-28 pb-12 hero-canvas border-b border-zinc-200 overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb lastLabel={webinar.title} />
          {/* Two-column layout: Content (left) + AI Summary (right) */}
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left Column - Main Content (60%) */}
            <div className="lg:col-span-3">
              {/* Badges Row */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {webinar.webinar_code && (
                  <Badge 
                    className="bg-zinc-50 text-zinc-900 border border-zinc-200 cursor-pointer hover:bg-zinc-100"
                    onClick={copyWebinarCode}
                  >
                    <Hash className="w-3 h-3 mr-1" />
                    {webinar.webinar_code}
                    <Copy className="w-3 h-3 ml-2" />
                  </Badge>
                )}
                {webinar.webinar_type === 'live' && (
                  <Badge className="bg-red-500 text-white">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse mr-1.5" />
                    Live Event
                  </Badge>
                )}
                {webinar.webinar_type !== 'live' && (
                  <Badge className="bg-blue-500 text-white">
                    <Play className="w-3 h-3 mr-1" />
                    Recorded
                  </Badge>
                )}
                {webinar.level && (
                  <Badge className={levelColors[webinar.level] || levelColors.intermediate}>
                    <Signal className="w-3 h-3 mr-1" />
                    {levelLabels[webinar.level] || levelLabels.intermediate}
                  </Badge>
                )}
                {!webinar.level && (
                  <Badge className={levelColors.intermediate}>
                    <Signal className="w-3 h-3 mr-1" />
                    {levelLabels.intermediate}
                  </Badge>
                )}
                {/* HRCI/CPE Badge - Only for RECORDED webinars */}
                {webinar.webinar_type !== 'live' && isHRIndustry(webinar.industry_name, webinar.sub_industry_name) && webinar.cpe_credits > 0 && (
                  <div className="flex items-center gap-1.5 bg-white rounded-md px-2 py-1 shadow-sm">
                    <img 
                      src={HRCI_LOGO_URL}
                      alt="HRCI Approved"
                      className="w-6 h-6"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-xs font-semibold text-navy-800">CPE Credits</span>
                  </div>
                )}
                {(webinar.sub_industry_name || webinar.industry_name) && (
                  <Badge variant="outline" className="border-zinc-300 text-zinc-900">
                    {webinar.sub_industry_name || webinar.industry_name}
                  </Badge>
                )}
              </div>

              {/* Title - Full width of left column */}
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-zinc-900 mb-6">
                {webinar.title}
              </h1>

              {/* Meta Info - Date, Time, Duration */}
              {webinar.webinar_type === 'live' && (
              <>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                  <Calendar className="w-5 h-5 text-navy-600" />
                  <span className="text-zinc-700">{formatDate(webinar.scheduled_date)}</span>
                </div>
                <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                  <Timer className="w-5 h-5 text-navy-600" />
                  <span className="text-zinc-700">{formatTime(webinar.scheduled_date)} {getETAbbr(webinar.scheduled_date)}</span>
                </div>
                <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                  <Hourglass className="w-5 h-5 text-navy-600" />
                  <span className="text-zinc-700">{webinar.duration_minutes} min</span>
                </div>
              </div>
              
              {/* Actions Row - Add to Calendar & Share */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-zinc-300 text-zinc-900 hover:bg-zinc-50 rounded-xl"
                  onClick={addToGoogleCalendar}
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Add to Calendar
                </Button>
                <div className="relative">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-zinc-300 text-zinc-900 hover:bg-zinc-50 rounded-xl"
                    onClick={() => setShowShareMenu(!showShareMenu)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  {showShareMenu && (
                    <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl p-2 z-50 min-w-48">
                      <button 
                        onClick={() => shareOnSocial('facebook')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Facebook className="w-4 h-4 text-blue-600" />
                        Facebook
                      </button>
                      <button 
                        onClick={() => shareOnSocial('linkedin')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Linkedin className="w-4 h-4 text-blue-700" />
                        LinkedIn
                      </button>
                      <button 
                        onClick={() => shareOnSocial('twitter')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Twitter className="w-4 h-4 text-sky-500" />
                        Twitter
                      </button>
                      <button 
                        onClick={() => shareOnSocial('email')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Mail className="w-4 h-4 text-slate-600" />
                        Email
                      </button>
                    </div>
                  )}
                </div>
              </div>
              </>
            )}
            
            {/* Duration + Actions for recorded webinars - Two rows */}
            {webinar.webinar_type !== 'live' && (
              <>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                  <Hourglass className="w-5 h-5 text-navy-600" />
                  <span className="text-zinc-700">{webinar.duration_minutes} min</span>
                </div>
                <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                  <Play className="w-5 h-5 text-navy-600" />
                  <span className="text-zinc-700">Watch Anytime</span>
                </div>
                {/* HRCI Badge for recorded webinars with CPE credits */}
                {isHRIndustry(webinar.industry_name, webinar.sub_industry_name) && webinar.cpe_credits > 0 && (
                  <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-2 shadow-sm">
                    <img 
                      src={HRCI_LOGO_URL}
                      alt="HRCI Approved"
                      className="w-6 h-6"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-navy-800 font-semibold">CPE Credits</span>
                  </div>
                )}
              </div>
              
              {/* Actions Row */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-zinc-300 text-zinc-900 hover:bg-zinc-50 rounded-xl"
                  onClick={addToGoogleCalendar}
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Add to Calendar
                </Button>
                <div className="relative">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-zinc-300 text-zinc-900 hover:bg-zinc-50 rounded-xl"
                    onClick={() => setShowShareMenu(!showShareMenu)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  {showShareMenu && (
                    <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl p-2 z-50 min-w-48">
                      <button 
                        onClick={() => shareOnSocial('facebook')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Facebook className="w-4 h-4 text-blue-600" />
                        Facebook
                      </button>
                      <button 
                        onClick={() => shareOnSocial('linkedin')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Linkedin className="w-4 h-4 text-blue-700" />
                        LinkedIn
                      </button>
                      <button 
                        onClick={() => shareOnSocial('twitter')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Twitter className="w-4 h-4 text-sky-500" />
                        Twitter
                      </button>
                      <button 
                        onClick={() => shareOnSocial('email')}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                      >
                        <Mail className="w-4 h-4 text-slate-600" />
                        Email
                      </button>
                    </div>
                  )}
                </div>
              </div>
              </>
            )}

            {/* Speakers */}
            {webinar.speakers?.length > 0 && (
              <div className="pt-6 border-t border-zinc-200">
                <h3 className="text-zinc-900 font-medium mb-4">Presented by</h3>
                <div className="flex flex-wrap gap-4">
                  {webinar.speakers.map((speaker) => (
                    <Link
                      key={speaker.id}
                      to={`/speakers/${speaker.slug || speaker.id}`}
                      className="flex items-center gap-3 bg-zinc-50 rounded-xl p-4 hover:bg-zinc-100 transition-colors"
                    >
                      <img
                        src={resolveImageUrl(speaker.profile_image) || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100'}
                        alt={speaker.first_name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-navy-300"
                      />
                      <div>
                        <p className="text-zinc-900 font-semibold">
                          {speaker.first_name} {speaker.last_name}
                        </p>
                        <p className="text-zinc-500 text-sm">
                          {speaker.expertise_areas?.slice(0, 2).join(' • ')}
                        </p>
                        {speaker.years_experience && (
                          <p className="text-navy-600 text-xs mt-1">
                            {speaker.years_experience}+ years experience
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

            {/* Right Column - AI Summary (40%) */}
            <div className="lg:col-span-2">
              {(webinar.ai_summary?.overview || webinar.ai_summary?.takeaways?.length > 0) && (
                <div className="bg-[#09090b] rounded-2xl p-6 border border-zinc-800">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-emerald-400" />
                    </div>
                    <h3 className="text-white font-semibold">Quick Summary</h3>
                  </div>
                  
                  {webinar.ai_summary?.overview && (
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                      {webinar.ai_summary.overview}
                    </p>
                  )}
                  
                  {webinar.ai_summary?.takeaways?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-white text-sm font-medium">Key Takeaways:</h4>
                      <ul className="space-y-2">
                        {webinar.ai_summary.takeaways.map((takeaway, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-zinc-400">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            <span>{takeaway}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Placeholder when no summary - shows on mobile only when there's no summary */}
              {!webinar.ai_summary?.overview && !webinar.ai_summary?.takeaways?.length && webinar.webinar_type === 'live' && (
                <div className="hidden lg:block bg-zinc-50 rounded-2xl p-6 border border-zinc-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-zinc-500" />
                    </div>
                    <h3 className="text-zinc-500 font-medium">Quick Summary</h3>
                  </div>
                  <p className="text-slate-500 text-sm italic">
                    AI-generated summary coming soon...
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Unified Scroll-Based Accordion for all devices */}
              <Card className="border-slate-200 rounded-2xl overflow-hidden" ref={accordionRef}>
                <Accordion 
                  type="single" 
                  collapsible 
                  value={activeSection}
                  onValueChange={setActiveSection}
                  className="w-full"
                >
                  {/* Overview Section */}
                  <AccordionItem value="overview" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-navy-100 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-navy-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Webinar Overview
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px] prose prose-slate max-w-none">
                        {(webinar.overview || webinar.description || '').includes('<') ? (
                          <div 
                            className="text-slate-600 leading-relaxed text-sm md:text-base"
                            dangerouslySetInnerHTML={{ __html: webinar.overview || webinar.description || '' }}
                          />
                        ) : (
                          <p className="text-slate-600 leading-relaxed text-sm md:text-base whitespace-pre-wrap">
                            {webinar.overview || webinar.description || ''}
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Areas Covered Section */}
                  <AccordionItem value="areas" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <Target className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Areas Covered
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px]">
                        {(() => {
                          let areas = webinar.areas_covered;
                          
                          // Handle array type - join if array contains HTML strings
                          if (Array.isArray(areas) && areas.length > 0) {
                            // Check if array items contain HTML
                            const firstItem = areas[0];
                            if (typeof firstItem === 'string' && (firstItem.includes('<ul') || firstItem.includes('<li'))) {
                              // Join array items and process as HTML
                              areas = areas.join('');
                            } else {
                              // Plain text array - render as list
                              return (
                                <ul className="space-y-3">
                                  {areas.map((area, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                      <span className="text-slate-600 text-sm md:text-base">{area}</span>
                                    </li>
                                  ))}
                                </ul>
                              );
                            }
                          }
                          
                          if (typeof areas === 'string' && areas.length > 0) {
                            // Clean and format HTML content
                            let cleanContent = areas
                              // Decode HTML entities if present
                              .replace(/&lt;/g, '<')
                              .replace(/&gt;/g, '>')
                              .replace(/&amp;/g, '&')
                              .replace(/&quot;/g, '"')
                              .replace(/&#39;/g, "'");
                            
                            // Check if content has list tags
                            if (cleanContent.includes('<ul') || cleanContent.includes('<li')) {
                              // Style the list
                              const styledContent = cleanContent
                                .replace(/<ul[^>]*>/g, '<ul class="space-y-3 list-none">')
                                .replace(/<li[^>]*>/g, '<li class="flex items-start gap-3"><svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span class="text-slate-600 text-sm md:text-base">')
                                .replace(/<\/li>/g, '</span></li>');
                              return (
                                <div 
                                  className="prose prose-slate max-w-none"
                                  dangerouslySetInnerHTML={{ __html: styledContent }}
                                />
                              );
                            }
                            
                            // If content is plain text or has <p> tags, render appropriately
                            if (cleanContent.includes('<p')) {
                              return (
                                <div 
                                  className="prose prose-slate max-w-none text-sm md:text-base text-slate-600"
                                  dangerouslySetInnerHTML={{ __html: cleanContent }}
                                />
                              );
                            }
                            
                            // Plain text - display as is
                            return <p className="text-slate-600 text-sm md:text-base">{cleanContent}</p>;
                          }
                          return <p className="text-slate-500 text-sm">No areas covered listed.</p>;
                        })()}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Why Attend Section */}
                  <AccordionItem value="why" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Why Should You Attend?
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px] prose prose-slate max-w-none">
                        {(() => {
                          let content = webinar.why_attend || '';
                          // Decode HTML entities if present
                          content = content
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>')
                            .replace(/&amp;/g, '&')
                            .replace(/&quot;/g, '"')
                            .replace(/&#39;/g, "'");
                          
                          if (content.includes('<')) {
                            // Style lists if present
                            if (content.includes('<ul') || content.includes('<li')) {
                              content = content
                                .replace(/<ul[^>]*>/g, '<ul class="space-y-3 list-none">')
                                .replace(/<li[^>]*>/g, '<li class="flex items-start gap-3"><svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span class="text-slate-600 text-sm md:text-base">')
                                .replace(/<\/li>/g, '</span></li>');
                            }
                            return (
                              <div 
                                className="text-slate-600 leading-relaxed text-sm md:text-base"
                                dangerouslySetInnerHTML={{ __html: content }}
                              />
                            );
                          }
                          return (
                            <p className="text-slate-600 leading-relaxed text-sm md:text-base whitespace-pre-wrap">
                              {content || 'Gain valuable insights and practical knowledge from industry experts.'}
                            </p>
                          );
                        })()}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Who Should Attend Section */}
                  <AccordionItem value="who" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <UserCheck className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Who Should Attend?
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px]">
                        {(() => {
                          let who = webinar.who_should_attend || '';
                          // Decode HTML entities if present
                          who = who
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>')
                            .replace(/&amp;/g, '&')
                            .replace(/&quot;/g, '"')
                            .replace(/&#39;/g, "'");
                          
                          if (who.includes('<ul') || who.includes('<li')) {
                            const styledContent = who
                              .replace(/<ul[^>]*>/g, '<ul class="space-y-3 list-none">')
                              .replace(/<li[^>]*>/g, '<li class="flex items-start gap-3"><svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span class="text-slate-600 text-sm md:text-base">')
                              .replace(/<\/li>/g, '</span></li>');
                            return (
                              <div 
                                className="prose prose-slate max-w-none"
                                dangerouslySetInnerHTML={{ __html: styledContent }}
                              />
                            );
                          }
                          if (who.includes('<')) {
                            return (
                              <div 
                                className="prose prose-slate max-w-none text-sm md:text-base text-slate-600"
                                dangerouslySetInnerHTML={{ __html: who }}
                              />
                            );
                          }
                          return (
                            <p className="text-slate-600 leading-relaxed text-sm md:text-base whitespace-pre-wrap">
                              {who || 'Professionals seeking to enhance their knowledge and skills.'}
                            </p>
                          );
                        })()}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Learning Objectives Section */}
                  <AccordionItem value="objectives" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gold-100 flex items-center justify-center">
                          <Target className="w-4 h-4 md:w-5 md:h-5 text-gold-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Learning Objectives
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px]">
                        {(() => {
                          const objectives = webinar.learning_objectives_text || webinar.learning_objectives;
                          if (typeof objectives === 'string' && objectives.length > 0) {
                            let counter = 0;
                            const styledContent = objectives
                              .replace(/<ul>/g, '<ol class="space-y-3 list-none">')
                              .replace(/<\/ul>/g, '</ol>')
                              .replace(/<li>/g, () => {
                                counter++;
                                return `<li class="flex items-start gap-3"><span class="w-6 h-6 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">${counter}</span><span class="text-slate-600">`;
                              })
                              .replace(/<\/li>/g, '</span></li>');
                            return (
                              <div 
                                className="prose prose-slate max-w-none text-sm md:text-base"
                                dangerouslySetInnerHTML={{ __html: styledContent }}
                              />
                            );
                          }
                          if (Array.isArray(objectives) && objectives.length > 0) {
                            // Join array items and check if they contain HTML
                            const joined = objectives.join('');
                            if (joined.includes('<')) {
                              let counter = 0;
                              const styledContent = joined
                                .replace(/<ul>/g, '<ol class="space-y-3 list-none">')
                                .replace(/<\/ul>/g, '</ol>')
                                .replace(/<li>/g, () => {
                                  counter++;
                                  return `<li class="flex items-start gap-3"><span class="w-6 h-6 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">${counter}</span><span class="text-slate-600">`;
                                })
                                .replace(/<\/li>/g, '</span></li>');
                              return (
                                <div 
                                  className="prose prose-slate max-w-none text-sm md:text-base"
                                  dangerouslySetInnerHTML={{ __html: styledContent }}
                                />
                              );
                            }
                            return (
                              <ol className="space-y-3">
                                {objectives.map((obj, idx) => (
                                  <li key={idx} className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">
                                      {idx + 1}
                                    </span>
                                    <span className="text-slate-600 text-sm md:text-base">{obj}</span>
                                  </li>
                                ))}
                              </ol>
                            );
                          }
                          return <p className="text-slate-500 text-sm">Gain practical knowledge applicable to your role.</p>;
                        })()}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Background Section */}
                  <AccordionItem value="background" className="border-0">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                          <FileText className="w-4 h-4 md:w-5 md:h-5 text-slate-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Topic Background
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px] prose prose-slate max-w-none">
                        {(webinar.topic_background || '').includes('<') ? (
                          <div 
                            className="text-slate-600 leading-relaxed text-sm md:text-base"
                            dangerouslySetInnerHTML={{ __html: webinar.topic_background || '' }}
                          />
                        ) : (
                          <p className="text-slate-600 leading-relaxed text-sm md:text-base whitespace-pre-wrap">
                            {webinar.topic_background || webinar.description || ''}
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>


              {/* Speaker Section */}
              {webinar.speakers?.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-heading text-2xl font-bold text-navy-700 mb-6">
                    About Your Instructor{webinar.speakers.length > 1 ? 's' : ''}
                  </h2>
                  <div className="space-y-6">
                    {webinar.speakers.map((speaker) => (
                      <Card key={speaker.id} className="border-slate-200 rounded-2xl overflow-hidden">
                        <CardContent className="p-0">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 h-48 md:h-auto relative">
                              <img
                                src={resolveImageUrl(speaker.profile_image) || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'}
                                alt={speaker.first_name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'; }}
                              />
                              {speaker.is_featured && (
                                <div className="absolute top-3 left-3">
                                  <Badge className="bg-gold-500 text-navy-800">
                                    <Star className="w-3 h-3 mr-1 fill-current" />
                                    Featured
                                  </Badge>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 p-6">
                              <Link to={`/speakers/${speaker.slug || speaker.id}`}>
                                <h3 className="font-heading text-xl font-bold text-navy-700 mb-1 hover:text-gold-600 transition-colors">
                                  {speaker.first_name} {speaker.last_name}
                                </h3>
                              </Link>
                              <p className="text-gold-600 font-medium mb-3">
                                {speaker.expertise_areas?.join(' • ')}
                              </p>
                              <p className="text-slate-600 mb-4 line-clamp-3">
                                {speaker.bio || speaker.headline}
                              </p>
                              <div className="flex flex-wrap items-center gap-4 text-sm">
                                <span className="flex items-center gap-1 text-slate-500">
                                  <Award className="w-4 h-4 text-navy-500" />
                                  {speaker.years_experience || 15}+ Years
                                </span>
                                <span className="flex items-center gap-1 text-slate-500">
                                  <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
                                  {speaker.rating?.toFixed(1) || '4.9'} Rating
                                </span>
                                <Link 
                                  to={`/speakers/${speaker.slug || speaker.id}`}
                                  className="text-navy-600 hover:text-gold-600 font-medium flex items-center gap-1"
                                >
                                  View Profile
                                  <ChevronRight className="w-4 h-4" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Pricing Card (Normal, not sticky) */}
            <div className="lg:col-span-1">
              {/* Pricing Card */}
              <Card className="border-slate-200 rounded-2xl overflow-hidden">
                <div className="bg-navy-800 p-4">
                  <h3 className="font-heading text-lg font-bold text-white">
                    Select Your Access
                  </h3>
                </div>
                <CardContent className="p-6">
                  {isRegistered ? (
                    <div className="text-center py-4">
                      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-navy-700 mb-2">
                        You're Registered!
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">
                        Check your email for joining instructions.
                      </p>
                      <Link to="/dashboard/webinars">
                        <Button className="w-full bg-navy-600 hover:bg-navy-700 text-white rounded-xl">
                          View My Webinars
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* Pricing Options */}
                      <div className="space-y-3 mb-6">
                        {pricingOptions.map((option) => (
                          <div
                            key={option.id}
                            onClick={() => setSelectedPricing(option.id)}
                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              selectedPricing === option.id
                                ? 'border-gold-500 bg-gold-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  selectedPricing === option.id ? 'bg-gold-500 text-navy-800' : 'bg-slate-100 text-slate-600'
                                }`}>
                                  <option.icon className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="font-semibold text-navy-700">{option.name}</p>
                                  <p className="text-xs text-slate-500">{option.description}</p>
                                  {option.participantInfo && (
                                    <p className="text-xs text-gold-600 font-medium mt-0.5">{option.participantInfo}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-xl text-navy-700">${option.price}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <Button
                        className="w-full h-14 bg-gold-500 hover:bg-gold-600 text-navy-800 font-bold text-lg rounded-xl"
                        onClick={handleRegister}
                        disabled={addingToCart}
                        data-testid="register-btn"
                      >
                        {addingToCart ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="mr-2 h-5 w-5" />
                            Register Now - ${selectedOption?.price || 0}
                          </>
                        )}
                      </Button>

                      {/* Features */}
                      {selectedOption && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <div className="space-y-2">
                            {selectedOption.features.map((feature, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* More from This Speaker - Full Width (spans both columns) */}
          {webinar.speaker_webinars?.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold text-navy-700">
                  More from {webinar.speakers?.[0]?.first_name} {webinar.speakers?.[0]?.last_name || 'This Speaker'}
                </h2>
                <div className="flex items-center gap-3">
                  {webinar.speaker_webinars.length > 3 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSpeakerCarouselIndex(Math.max(0, speakerCarouselIndex - 1))}
                        disabled={speakerCarouselIndex === 0}
                        className="rounded-full w-10 h-10 p-0"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSpeakerCarouselIndex(Math.min(Math.ceil(webinar.speaker_webinars.length / 3) - 1, speakerCarouselIndex + 1))}
                        disabled={speakerCarouselIndex >= Math.ceil(webinar.speaker_webinars.length / 3) - 1}
                        className="rounded-full w-10 h-10 p-0"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  )}
                  {/* View All from Speaker Button */}
                  {webinar.speakers?.[0]?.id && (
                    <Link to={`/speakers/${webinar.speakers[0].slug || webinar.speakers[0].id}`}>
                      <Button variant="outline" size="sm" className="text-navy-600 border-navy-300 hover:bg-navy-50">
                        View All from {webinar.speakers[0].first_name}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {webinar.speaker_webinars.slice(speakerCarouselIndex * 3, speakerCarouselIndex * 3 + 3).map((w, idx) => (
                  <WebinarCard key={w.id} item={w} type="webinar" animationDelay={idx * 0.05} />
                ))}
              </div>
            </div>
          )}

          {/* Similar in Industry - Full Width (spans both columns) */}
          {webinar.industry_webinars?.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold text-navy-700">
                  Similar in {webinar.industry_name}
                </h2>
                {webinar.industry_webinars.length > 3 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIndustryCarouselIndex(Math.max(0, industryCarouselIndex - 1))}
                      disabled={industryCarouselIndex === 0}
                      className="rounded-full w-10 h-10 p-0"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIndustryCarouselIndex(Math.min(Math.ceil(webinar.industry_webinars.length / 3) - 1, industryCarouselIndex + 1))}
                      disabled={industryCarouselIndex >= Math.ceil(webinar.industry_webinars.length / 3) - 1}
                      className="rounded-full w-10 h-10 p-0"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {webinar.industry_webinars.slice(industryCarouselIndex * 3, industryCarouselIndex * 3 + 3).map((w, idx) => (
                  <WebinarCard key={w.id} item={w} type="webinar" animationDelay={idx * 0.05} />
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link to={`/industries/${webinar.industry?.slug || webinar.industry_id}`}>
                  <Button variant="outline" className="rounded-xl">
                    View All {webinar.industry_name} Webinars
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
